/**
 * Family Board Feed Component
 *
 * Facebook/Instagram-style social feed for the family.
 * Features:
 * - Create posts with text (photos coming in Phase 2)
 * - Display posts in real-time feed
 * - Like/unlike posts
 * - Comment on posts
 * - Real-time updates using Firestore onSnapshot
 *
 * Phase 1: Text posts, likes, comments
 * Phase 2: Photo/video uploads, media grid
 * Phase 3: Advanced features (reactions, sharing, mentions)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
  IconButton,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputAdornment
} from '@mui/material';
import {
  PhotoCamera as PhotoIcon,
  Videocam as VideoIcon,
  EmojiEmotions as EmojiIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { boardService } from '../../services/boardService';
import PostCard from './PostCard';
import EmojiPicker from '../../Components/EmojiPicker';
import logger from '../../utils/logger';

const FamilyBoardFeed = () => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Refs
  const photoInputRef = useRef(null);

  // State management
  const [posts, setPosts] = useState([]);
  const [newPostText, setNewPostText] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [feelingMenuAnchor, setFeelingMenuAnchor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Available feelings for posts
  const feelings = [
    { emoji: '😊', label: 'Happy' },
    { emoji: '😢', label: 'Sad' },
    { emoji: '🎉', label: 'Excited' },
    { emoji: '😴', label: 'Tired' },
    { emoji: '🙏', label: 'Grateful' },
    { emoji: '❤️', label: 'Loved' },
    { emoji: '😂', label: 'Laughing' },
    { emoji: '😡', label: 'Angry' },
    { emoji: '😮', label: 'Surprised' },
    { emoji: '🤔', label: 'Thinking' },
    { emoji: '💪', label: 'Motivated' },
    { emoji: '😌', label: 'Peaceful' }
  ];

  /**
   * Subscribe to posts in real-time
   */
  useEffect(() => {
    if (!profile?.familyCode) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = boardService.subscribeToPosts(
      profile.familyCode,
      (updatedPosts) => {
        setPosts(updatedPosts);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [profile?.familyCode]);

  /**
   * Handle photo selection
   */
  const handlePhotoSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate files (max 5 photos, max 10MB each)
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Only image files are allowed' });
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'Each photo must be under 10MB' });
        return false;
      }
      return true;
    });

    if (selectedPhotos.length + validFiles.length > 5) {
      setSnackbar({ open: true, message: 'Maximum 5 photos per post' });
      return;
    }

    // Create previews
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setSelectedPhotos(prev => [...prev, ...validFiles]);
    setPhotoPreview(prev => [...prev, ...newPreviews]);

    // Reset input
    event.target.value = '';
  };

  /**
   * Remove a selected photo
   */
  const handleRemovePhoto = (index) => {
    URL.revokeObjectURL(photoPreview[index]);
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreview(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Handle feeling menu open
   */
  const handleFeelingClick = (event) => {
    setFeelingMenuAnchor(event.currentTarget);
  };

  /**
   * Handle feeling selection from menu
   */
  const handleFeelingSelect = (feeling) => {
    setSelectedFeeling(`${feeling.emoji} ${feeling.label}`);
    setFeelingMenuAnchor(null);
  };

  /**
   * Handle feeling menu close
   */
  const handleFeelingMenuClose = () => {
    setFeelingMenuAnchor(null);
  };

  /**
   * Upload photos to Firebase Storage
   */
  const uploadPhotos = async (files) => {
    const uploadPromises = files.map(async (file) => {
      const timestamp = Date.now();
      const storageRef = ref(storage, `families/${profile.familyCode}/posts/${timestamp}_${file.name}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    });
    return Promise.all(uploadPromises);
  };

  /**
   * Handle creating a new post
   */
  const handleCreatePost = useCallback(async () => {
    if (!newPostText.trim() && selectedPhotos.length === 0) {
      setSnackbar({ open: true, message: 'Please add text or photos to your post' });
      return;
    }

    if (!profile?.familyCode || !currentUser?.uid) {
      setError('Unable to create post. Please refresh and try again.');
      return;
    }

    setPosting(true);
    setError(null);

    try {
      // Upload photos if any
      let mediaUrls = [];
      if (selectedPhotos.length > 0) {
        mediaUrls = await uploadPhotos(selectedPhotos);
      }

      const authorData = {
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        displayName: profile.displayName || `${profile.firstName} ${profile.lastName}`,
        profilePictureUrl: profile.profilePictureUrl || ''
      };

      // Build post text with feeling if selected
      let postText = newPostText;
      if (selectedFeeling) {
        postText = `${selectedFeeling}\n\n${newPostText}`;
      }

      await boardService.createPost(
        profile.familyCode,
        currentUser.uid,
        authorData,
        {
          text: postText,
          media: mediaUrls,
          photos: mediaUrls,
          visibility: 'all'
        }
      );

      // Clear form
      setNewPostText('');
      setSelectedPhotos([]);
      photoPreview.forEach(url => URL.revokeObjectURL(url));
      setPhotoPreview([]);
      setSelectedFeeling(null);
    } catch (err) {
      logger.error('Error creating post:', err);
      setError(err.message || 'Failed to create post. Please try again.');
    } finally {
      setPosting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPostText, selectedPhotos, selectedFeeling, profile, currentUser, photoPreview]);

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyPress = (e) => {
    // Ctrl+Enter or Cmd+Enter to post
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleCreatePost();
    }
  };

  /**
   * Handle emoji selection - insert into text at cursor position
   */
  const handleEmojiSelect = (emoji) => {
    setNewPostText(prev => prev + emoji);
  };

  // Show error if no family
  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to view the family board.</Alert>
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please join a family to view the family board.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: 600, md: 700 },
        mx: 'auto',
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 2, md: 3 }
      }}
    >
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Family Board
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Share updates, photos, and memories with your family
        </Typography>
      </Box>

      {/* Create Post Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Avatar */}
            <Avatar
              src={profile?.profilePictureUrl}
              sx={{ width: 40, height: 40 }}
            >
              {profile?.firstName?.[0]}{profile?.lastName?.[0]}
            </Avatar>

            {/* Post Input */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={6}
                placeholder={`What's on your mind, ${profile?.firstName}?`}
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 1 }}>
                      <EmojiPicker onEmojiSelect={handleEmojiSelect} size="small" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.default'
                  }
                }}
              />

              {/* Photo Previews */}
              {photoPreview.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {photoPreview.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePhoto(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'background.paper',
                          boxShadow: 1,
                          '&:hover': { bgcolor: 'error.light', color: 'white' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Selected Feeling */}
              {selectedFeeling && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="primary">
                    Feeling: {selectedFeeling}
                  </Typography>
                  <IconButton size="small" onClick={() => setSelectedFeeling(null)}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 2 : 0
                }}
              >
                {/* Media buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={photoInputRef}
                    onChange={handlePhotoSelect}
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                  />
                  <Button
                    startIcon={<PhotoIcon />}
                    size="small"
                    onClick={() => photoInputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                    color={selectedPhotos.length > 0 ? 'primary' : 'inherit'}
                  >
                    Photo {selectedPhotos.length > 0 && `(${selectedPhotos.length})`}
                  </Button>
                  <Button
                    startIcon={<VideoIcon />}
                    size="small"
                    onClick={() => setSnackbar({ open: true, message: 'Video upload coming soon!' })}
                    sx={{ textTransform: 'none' }}
                  >
                    Video
                  </Button>
                  <Button
                    startIcon={<EmojiIcon />}
                    size="small"
                    onClick={handleFeelingClick}
                    sx={{ textTransform: 'none' }}
                    color={selectedFeeling ? 'primary' : 'inherit'}
                  >
                    Feeling
                  </Button>
                </Box>

                {/* Feeling Picker Menu */}
                <Menu
                  anchorEl={feelingMenuAnchor}
                  open={Boolean(feelingMenuAnchor)}
                  onClose={handleFeelingMenuClose}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  PaperProps={{
                    sx: {
                      maxHeight: 300,
                      minWidth: 200
                    }
                  }}
                >
                  {feelings.map((feeling) => (
                    <MenuItem
                      key={feeling.label}
                      onClick={() => handleFeelingSelect(feeling)}
                      selected={selectedFeeling === `${feeling.emoji} ${feeling.label}`}
                    >
                      <ListItemIcon sx={{ minWidth: 36, fontSize: '1.25rem' }}>
                        {feeling.emoji}
                      </ListItemIcon>
                      <ListItemText>{feeling.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>

                {/* Post Button */}
                <Button
                  variant="contained"
                  onClick={handleCreatePost}
                  disabled={posting || (!newPostText.trim() && selectedPhotos.length === 0)}
                  fullWidth={isMobile}
                  sx={{ minWidth: 100 }}
                >
                  {posting ? <CircularProgress size={20} color="inherit" /> : 'Post'}
                </Button>
              </Box>

              {/* Hint text */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Press Ctrl+Enter (Cmd+Enter on Mac) to post
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && posts.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'background.default',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No posts yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Be the first to share something with your family!
          </Typography>
        </Paper>
      )}

      {/* Posts Feed */}
      {!loading && posts.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              familyCode={profile.familyCode}
              currentUserId={currentUser.uid}
            />
          ))}
        </Box>
      )}

      {/* Load More (Phase 2) */}
      {!loading && posts.length >= 20 && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button variant="outlined" disabled>
            Load More (Coming Soon)
          </Button>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setSnackbar({ ...snackbar, open: false })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default FamilyBoardFeed;
